import { useState, useCallback, useEffect } from 'react';
import { Card, CardHeader, Button, Badge, Input, Select, LoadingSkeleton, Pagination, Modal } from '@nexora/design-system';
import {
  useModuleCtx,
  useDepartmentalGoals,
  useCreateDepartmentalGoal,
  useUpdateDepartmentalGoal,
  useDeleteDepartmentalGoal,
  type DepartmentalGoal,
  type DepartmentalGoalInput,
  type GoalPeriodicity,
  GOAL_PERIODICITY_LABEL,
} from './useOkrs';
import { formatNumber } from '../../shared/utils/format';

/* ═══════════════════════════════════════════════════════════════════════════
   Metas Departamentais — Configuração de Metas e Objetivos
   (protótipo `erpone-configura-o-de-metas-e-objetivos`)
   ───────────────────────────────────────────────────────────────────────────
   Contratos consumidos (backend real, DTOs em okrs.dto.ts):
   - GET  /okrs/goals → PaginatedDepartmentalGoalResponseDto
         { data: DepartmentalGoalDto[], total, page, limit, totalPages, pesoTotal }
   - POST /okrs/goals, PATCH/DELETE /okrs/goals/:id → DepartmentalGoalDto

   Colunas: Departamento / KPI · Periodicity · Owner · Target · Weight (Total 100%).
   O backend valida a soma dos pesos (DEPARTMENTAL_GOAL_TOTAL_WEIGHT); a tela
   mostra `pesoTotal` do response e avisa quando diverge de 100%.
   ═══════════════════════════════════════════════════════════════════════════ */

interface ToastState {
  message: string;
  variant: 'success' | 'error';
}

function pesoLabel(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${formatNumber(value, { maximumFractionDigits: 1 })}%`;
}

const PERIODICITY_OPTIONS: { value: GoalPeriodicity; label: string }[] = [
  { value: 'MENSAL', label: 'Mensal' },
  { value: 'TRIMESTRAL', label: 'Trimestral' },
  { value: 'ANUAL', label: 'Anual' },
];

/* ── Modal: Criar / Editar meta ── */

function GoalFormModal({
  isOpen,
  onClose,
  goal,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  goal?: DepartmentalGoal;
  onSubmit: (data: DepartmentalGoalInput) => Promise<void>;
}): JSX.Element {
  const [departamento, setDepartamento] = useState('');
  const [kpi, setKpi] = useState('');
  const [periodicidade, setPeriodicidade] = useState<GoalPeriodicity>('TRIMESTRAL');
  const [owner, setOwner] = useState('');
  const [target, setTarget] = useState('');
  const [peso, setPeso] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (goal) {
      setDepartamento(goal.departamento);
      setKpi(goal.kpi);
      setPeriodicidade(goal.periodicidade);
      setOwner(goal.owner);
      setTarget(String(goal.target));
      setPeso(String(goal.peso));
    } else {
      setDepartamento('');
      setKpi('');
      setPeriodicidade('TRIMESTRAL');
      setOwner('');
      setTarget('');
      setPeso('');
    }
    setError(null);
  }, [goal, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!departamento.trim()) { setError('Departamento é obrigatório.'); return; }
    if (!kpi.trim()) { setError('KPI é obrigatório.'); return; }
    if (!owner.trim()) { setError('Responsável (owner) é obrigatório.'); return; }
    const targetNum = Number(target);
    if (!Number.isFinite(targetNum)) { setError('Meta (target) deve ser numérica.'); return; }
    const pesoNum = Number(peso);
    if (peso.trim() !== '' && (!Number.isFinite(pesoNum) || pesoNum < 0 || pesoNum > 100)) {
      setError('Peso deve estar entre 0 e 100%.');
      return;
    }
    try {
      setSubmitting(true);
      await onSubmit({
        departamento: departamento.trim(),
        kpi: kpi.trim(),
        periodicidade,
        owner: owner.trim(),
        target: targetNum,
        peso: peso.trim() === '' ? undefined : pesoNum,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar meta.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={goal ? 'Editar Meta Departamental' : 'Nova Meta Departamental'} size="md">
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Departamento" value={departamento} onChange={(e) => setDepartamento(e.target.value)} required placeholder="Ex.: Vendas" />
            <Input label="KPI" value={kpi} onChange={(e) => setKpi(e.target.value)} required placeholder="Ex.: ARR Growth" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Select label="Periodicidade" value={periodicidade} onChange={(e) => setPeriodicidade(e.target.value as GoalPeriodicity)}>
              {PERIODICITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
            <Input label="Responsável (owner)" value={owner} onChange={(e) => setOwner(e.target.value)} required placeholder="Ex.: A. Silva" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Meta (target)" type="number" value={target} onChange={(e) => setTarget(e.target.value)} required placeholder="Ex.: 2500000" />
            <Input label="Peso (%)" type="number" value={peso} onChange={(e) => setPeso(e.target.value)} placeholder="Ex.: 40" min={0} max={100} />
          </div>
          {error && (
            <div style={{ padding: '0.625rem 0.75rem', borderRadius: '8px', background: '#ffdad6', border: '1px solid #ffb4ab', color: '#ba1a1a', fontSize: '0.8125rem' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Salvando...' : goal ? 'Salvar alterações' : 'Criar meta'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

/* ── Página ── */

export function DepartmentalGoalsPage(): JSX.Element {
  const ctx = useModuleCtx();
  const [page, setPage] = useState(1);
  const [deptFilter, setDeptFilter] = useState('');
  const { data, isLoading, error } = useDepartmentalGoals(ctx, {
    page,
    limit: 20,
    departamento: deptFilter || undefined,
  });
  const createMutation = useCreateDepartmentalGoal(ctx);
  const updateMutation = useUpdateDepartmentalGoal();
  const deleteMutation = useDeleteDepartmentalGoal(ctx);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<DepartmentalGoal | undefined>(undefined);
  const [toast, setToast] = useState<ToastState | null>(null);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const handleCreate = useCallback(
    async (input: DepartmentalGoalInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('Meta departamental criada com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao criar meta.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [createMutation, showToast],
  );

  const handleUpdate = useCallback(
    async (input: DepartmentalGoalInput) => {
      if (!editingGoal) return;
      try {
        await updateMutation.mutateAsync({ id: editingGoal.id, ...input });
        showToast('Meta atualizada com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao atualizar meta.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [editingGoal, updateMutation, showToast],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!window.confirm('Tem certeza que deseja excluir esta meta?')) return;
      try {
        await deleteMutation.mutateAsync(id);
        showToast('Meta excluída com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao excluir meta.';
        showToast(msg, 'error');
      }
    },
    [deleteMutation, showToast],
  );

  const goals = data?.data ?? [];
  const pesoTotal = data?.pesoTotal ?? 0;
  const pesoOk = Math.abs(pesoTotal - 100) < 0.01;

  // Agrupa por departamento para a visão hierárquica do protótipo
  const groups = new Map<string, DepartmentalGoal[]>();
  for (const g of goals) {
    const list = groups.get(g.departamento) ?? [];
    list.push(g);
    groups.set(g.departamento, list);
  }

  return (
    <div className="module-page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700, color: '#191c1e' }}>Metas e Objetivos</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#43474e', fontSize: '0.875rem' }}>
            Configuração de metas departamentais — {data?.total ?? 0} metas, peso total {pesoLabel(pesoTotal)}.
          </p>
        </div>
        <Button variant="primary" onClick={() => { setEditingGoal(undefined); setModalOpen(true); }}>+ Nova Meta</Button>
      </div>

      {/* Peso total warning */}
      {data && !pesoOk && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.625rem 1rem',
            borderRadius: '8px',
            background: '#ffddba',
            border: '1px solid #ffb763',
            color: '#8a4b00',
            fontSize: '0.8125rem',
          }}
        >
          Atenção: a soma dos pesos é {pesoLabel(pesoTotal)} — o backend espera 100% (validação{' '}
          DEPARTMENTAL_GOAL_TOTAL_WEIGHT). Ajuste os pesos das metas.
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Metas configuradas', value: String(data?.total ?? 0) },
          { label: 'Departamentos', value: String(data ? new Set(goals.map((g) => g.departamento)).size : 0) },
          { label: 'Peso total', value: pesoLabel(pesoTotal) },
          { label: 'Status', value: pesoOk ? '100% OK' : 'Ajustar pesos' },
        ].map((s) => (
          <div key={s.label} style={{ background: '#fff', border: '1px solid #e0e3e5', borderRadius: '10px', padding: '1rem' }}>
            <p style={{ margin: 0, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#74777f' }}>{s.label}</p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '1.5rem', fontWeight: 700, color: s.label === 'Status' && !pesoOk ? '#ed6c02' : '#191c1e' }}>
              {s.value}
            </p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#191c1e' }}>
          Hierarquia de Objetivos Departamentais
        </h2>
        <div style={{ width: '240px' }}>
          <Input
            label="Filtrar por departamento"
            value={deptFilter}
            onChange={(e) => { setDeptFilter(e.target.value); setPage(1); }}
            placeholder="Ex.: Vendas"
          />
        </div>
      </div>

      <Card variant="outlined" padding="0">
        <CardHeader className="px-md pt-md">
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Metas por departamento</h3>
        </CardHeader>
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
            <LoadingSkeleton />
            <span style={{ fontSize: '0.875rem' }}>Carregando metas...</span>
          </div>
        )}
        {error && !isLoading && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar metas</p>
            <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>
              {error instanceof Error ? error.message : 'Erro desconhecido'}
            </p>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()} style={{ marginTop: '1rem' }}>Tentar novamente</Button>
          </div>
        )}
        {!isLoading && !error && goals.length === 0 && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f' }}>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>Nenhuma meta departamental cadastrada.</p>
            <Button variant="outline" size="sm" onClick={() => { setEditingGoal(undefined); setModalOpen(true); }}>Criar primeira meta</Button>
          </div>
        )}
        {!isLoading && !error && goals.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Departamento / KPI', 'Periodicidade', 'Responsável', 'Meta (target)', 'Peso (Total 100%)', 'Ações'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: h === 'Meta (target)' || h === 'Peso (Total 100%)' ? 'right' : 'left',
                        padding: '0.75rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from(groups.entries()).map(([dept, list]) => (
                  <DeptGroup
                    key={dept}
                    dept={dept}
                    goals={list}
                    onEdit={(g) => { setEditingGoal(g); setModalOpen(true); }}
                    onDelete={(id) => handleDelete(id)}
                  />
                ))}
                {/* Total row */}
                <tr style={{ background: '#f2f4f6' }}>
                  <td colSpan={4} style={{ padding: '0.75rem 1rem', fontWeight: 700, color: '#191c1e' }}>
                    Peso total da configuração
                  </td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 700, color: pesoOk ? '#2e7d32' : '#ed6c02' }}>
                    {pesoLabel(pesoTotal)}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {!isLoading && !error && (data?.totalPages ?? 1) > 1 && (
          <div style={{ padding: '1rem', borderTop: '1px solid #eceef0' }}>
            <Pagination page={data?.page ?? 1} totalPages={data?.totalPages ?? 1} onChange={(p) => setPage(p)} />
          </div>
        )}
      </Card>

      <GoalFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        goal={editingGoal}
        onSubmit={editingGoal ? handleUpdate : handleCreate}
      />

      {toast && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            right: '1.5rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            background: toast.variant === 'success' ? '#d3f5d6' : '#ffdad6',
            border: `1px solid ${toast.variant === 'success' ? '#b6ebbb' : '#ffb4ab'}`,
            color: toast.variant === 'success' ? '#2e7d32' : '#ba1a1a',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 10000,
            maxWidth: '400px',
          }}
        >
          {toast.message}
          <button onClick={() => setToast(null)} aria-label="Fechar" style={{ marginLeft: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1rem', padding: 0, lineHeight: 1 }}>
            &times;
          </button>
        </div>
      )}

      <style>{`.module-page { padding: 1.5rem; }`}</style>
    </div>
  );
}

function DeptGroup({
  dept,
  goals,
  onEdit,
  onDelete,
}: {
  dept: string;
  goals: DepartmentalGoal[];
  onEdit: (g: DepartmentalGoal) => void;
  onDelete: (id: string) => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState(true);
  const deptPeso = goals.reduce((s, g) => s + (g.peso ?? 0), 0);

  return (
    <>
      <tr style={{ background: '#f2f4f6', cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <td colSpan={6} style={{ padding: '0.625rem 1rem', fontWeight: 700, color: '#002045', borderBottom: '1px solid #e0e3e5' }}>
          {expanded ? '▼' : '▶'} {dept}
          <span style={{ fontWeight: 400, color: '#74777f', fontSize: '0.75rem', marginLeft: '0.5rem' }}>
            {goals.length} {goals.length === 1 ? 'meta' : 'metas'} · peso {pesoLabel(deptPeso)}
          </span>
        </td>
      </tr>
      {expanded &&
        goals.map((g) => (
          <tr key={g.id} style={{ borderBottom: '1px solid #eceef0' }}>
            <td style={{ padding: '0.625rem 1rem', paddingLeft: '2.5rem', fontWeight: 500 }}>
              {g.kpi}
            </td>
            <td style={{ padding: '0.625rem 1rem', color: '#43474e' }}>
              <Badge variant="neutral" size="sm">{GOAL_PERIODICITY_LABEL[g.periodicidade]}</Badge>
            </td>
            <td style={{ padding: '0.625rem 1rem', color: '#191c1e' }}>{g.owner}</td>
            <td style={{ padding: '0.625rem 1rem', textAlign: 'right', fontWeight: 600 }}>
              {formatNumber(g.target, { maximumFractionDigits: 2 })}
            </td>
            <td style={{ padding: '0.625rem 1rem', textAlign: 'right' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <div style={{ width: '90px', height: '6px', background: '#e2e5e8', borderRadius: '999px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.max(0, Math.min(100, g.peso))}%`, background: '#002045', borderRadius: '999px' }} />
                </div>
                <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#191c1e', width: '44px', textAlign: 'right' }}>
                  {pesoLabel(g.peso)}
                </span>
              </div>
            </td>
            <td style={{ padding: '0.625rem 1rem', whiteSpace: 'nowrap', textAlign: 'right' }}>
              <Button variant="ghost" size="sm" onClick={() => onEdit(g)}>Editar</Button>
              <Button variant="ghost" size="sm" style={{ color: '#ba1a1a' }} onClick={() => onDelete(g.id)}>Excluir</Button>
            </td>
          </tr>
        ))}
    </>
  );
}